/**
 * <kk-product-buy> — behaviour of blocks/kk-product-buy.liquid.
 *
 * - Quantity stepper; the add-to-cart label shows the line total ("Add to cart · Rs. 480").
 * - Variant pills (only when the product has several variants): switch the variant, price and sold-out state, and
 *   announce kk:variant:change { variantId, mediaId } so the gallery can jump to the variant's image.
 * - Adding to the cart is done by the shared [data-kk-add-to-cart] handler in kk-cart.js (opens the cart drawer).
 * - "Buy it now" adds the items without opening the drawer, then goes straight to checkout.
 */
import { formatMoney } from './kk-money.js';

class KKProductBuy extends HTMLElement {
  connectedCallback() {
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.addButton = this.querySelector('[data-kk-add-to-cart]');
    this.addLabel = this.querySelector('[data-kk-buy-label]');
    this.nowButton = this.querySelector('[data-kk-buy-now]');
    this.qtyValue = this.querySelector('[data-kk-qty-value]');
    this.format = this.dataset.moneyFormat || '{{amount}}';
    this.labelTemplate = this.dataset.addLabel || 'Add to cart';
    this.soldOutLabel = this.dataset.soldOutLabel || 'Sold out';
    this.quantity = 1;

    try {
      this.variants = JSON.parse(this.querySelector('script[data-kk-variants]')?.textContent || '[]');
    } catch (error) {
      this.variants = [];
    }
    this.variant = this.variants.find((v) => String(v.id) === this.dataset.variantId) || this.variants[0];

    this.querySelectorAll('[data-kk-qty-step]').forEach((button) => {
      button.addEventListener('click', () => this.#setQuantity(this.quantity + Number(button.dataset.kkQtyStep)), { signal });
    });
    this.querySelectorAll('input[data-kk-variant-input]').forEach((input) => {
      input.addEventListener('change', () => this.#selectVariant(input.value), { signal });
    });
    this.nowButton?.addEventListener('click', () => this.#buyNow(), { signal });
    this.#render();
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  #setQuantity(value) {
    this.quantity = Math.max(1, Math.min(99, value));
    this.#render();
  }

  #selectVariant(id) {
    const variant = this.variants.find((v) => String(v.id) === String(id));
    if (!variant) return;
    this.variant = variant;
    this.#render();
    document.dispatchEvent(new CustomEvent('kk:variant:change', { detail: { variantId: variant.id, mediaId: variant.mediaId } }));
  }

  #render() {
    const variant = this.variant;
    if (this.qtyValue) this.qtyValue.textContent = String(this.quantity);
    if (!variant) return;

    const available = variant.available !== false;
    const total = formatMoney(variant.price * this.quantity, this.format, true);

    if (this.addButton) {
      this.addButton.dataset.variantId = String(variant.id);
      this.addButton.dataset.quantity = String(this.quantity);
      this.addButton.disabled = !available;
    }
    if (this.addLabel) this.addLabel.textContent = available ? this.labelTemplate.replace('[price]', total) : this.soldOutLabel;
    if (this.nowButton) this.nowButton.disabled = !available;
    this.querySelectorAll('[data-kk-qty-step]').forEach((button) => (button.disabled = !available));
  }

  async #buyNow() {
    const variant = this.variant;
    if (!variant || !window.KKCart) return;
    this.nowButton.setAttribute('aria-busy', 'true');
    try {
      await window.KKCart.add([{ id: variant.id, quantity: this.quantity }], { open: false, trigger: this.nowButton });
      window.location.assign(`${window.Shopify?.routes?.root || '/'}checkout`);
    } catch (error) {
      this.nowButton.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('kk-product-buy')) {
  customElements.define('kk-product-buy', KKProductBuy);
}
